import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User,
  Mail,
  Phone,
  Shield,
  ShieldCheck,
  ShieldOff,
  Key,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Building,
  Download,
  Trash2,
  FileText,
  AlertTriangle,
  Camera,
  Check,
  Upload,
  Image,
  X,
  Smartphone,
  QrCode,
  Copy,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

// Avatars prédéfinis - professionnels EHS/industrie
const PREDEFINED_AVATARS = [
  { id: 'default-blue', color: 'bg-blue-500', initials: true },
  { id: 'default-green', color: 'bg-green-500', initials: true },
  { id: 'default-purple', color: 'bg-purple-500', initials: true },
  { id: 'default-orange', color: 'bg-orange-500', initials: true },
  { id: 'default-red', color: 'bg-red-500', initials: true },
  { id: 'default-teal', color: 'bg-teal-500', initials: true },
  { id: 'default-pink', color: 'bg-pink-500', initials: true },
  { id: 'default-indigo', color: 'bg-indigo-500', initials: true },
  // Avatars avec icônes métiers
  { id: 'worker-helmet', icon: '👷', label: 'Ouvrier' },
  { id: 'engineer', icon: '🔧', label: 'Technicien' },
  { id: 'safety', icon: '🦺', label: 'HSE' },
  { id: 'manager', icon: '👔', label: 'Manager' },
  { id: 'doctor', icon: '🩺', label: 'Médical' },
  { id: 'forklift', icon: '🚜', label: 'Cariste' },
  { id: 'clipboard', icon: '📋', label: 'Contrôleur' },
  { id: 'factory', icon: '🏭', label: 'Production' },
];

export default function MonProfil() {
  const { currentUser, setCurrentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [avatarTab, setAvatarTab] = useState('predefined');
  
  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // 2FA State
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAAvailable, setTwoFAAvailable] = useState(false);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState('password'); // 'password', 'setup', 'confirm'
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    avatar: null,
    photo_url: null,
    photo_data: null,  // Base64 photo data
    has_photo: false   // Flag indicating photo exists
  });
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    loadProfile();
    load2FAStatus();
  }, [currentUser?.id]);

  const load2FAStatus = async () => {
    try {
      const status = await api.get2FAStatus();
      setTwoFAEnabled(status.enabled || false);
      setTwoFAAvailable(status.available !== false); // Default to true if not explicitly false
    } catch (error) {
      console.error('Failed to load 2FA status:', error);
      // Default to showing 2FA section - let the backend handle availability
      setTwoFAAvailable(true);
      setTwoFAEnabled(false);
    }
  };

  const loadProfile = async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      const data = await api.getProfile(currentUser.id);
      setProfileData({
        prenom: data.prenom || '',
        nom: data.nom || '',
        email: data.email || '',
        telephone: data.telephone || '',
        avatar: data.avatar || null,
        photo_url: data.photo_url || null,
        photo_data: data.photo_data || null,  // Base64 photo data from MongoDB
        has_photo: data.has_photo || false    // Flag indicating photo exists
      });
      setSelectedAvatar(data.avatar || null);
      // Set initial tab based on whether user has a photo
      if (data.photo_url || data.photo_data || data.has_photo) {
        setAvatarTab('photo');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!profileData.prenom.trim() || !profileData.nom.trim()) {
      toast.error('Le prénom et le nom sont obligatoires');
      return;
    }
    
    if (!profileData.email.trim() || !profileData.email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }
    
    try {
      setSaving(true);
      const updatedUser = await api.updateProfile(currentUser.id, profileData);
      
      // Update current user in context
      setCurrentUser(prev => ({
        ...prev,
        prenom: updatedUser.prenom,
        nom: updatedUser.nom,
        email: updatedUser.email,
        telephone: updatedUser.telephone
      }));
      
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword) {
      toast.error('Veuillez entrer votre mot de passe actuel');
      return;
    }
    
    if (!passwordData.newPassword) {
      toast.error('Veuillez entrer un nouveau mot de passe');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    try {
      setChangingPassword(true);
      await api.changePassword(currentUser.id, passwordData.currentPassword, passwordData.newPassword);
      
      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast.success('Mot de passe modifié avec succès');
    } catch (error) {
      toast.error(error.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-purple-100 text-purple-800',
      'HSE': 'bg-green-100 text-green-800',
      'Directeur': 'bg-blue-100 text-blue-800',
      'Manager': 'bg-orange-100 text-orange-800',
      'RH': 'bg-pink-100 text-pink-800',
      'Maintenance': 'bg-yellow-100 text-yellow-800',
      'Collaborateur': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const handleSaveAvatar = async () => {
    try {
      setSavingAvatar(true);
      await api.updateAvatar(currentUser.id, selectedAvatar);
      
      // Update current user in context
      setCurrentUser(prev => ({
        ...prev,
        avatar: selectedAvatar
      }));
      
      setProfileData(prev => ({ ...prev, avatar: selectedAvatar }));
      setShowAvatarDialog(false);
      toast.success('Avatar mis à jour avec succès');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la mise à jour de l\'avatar');
    } finally {
      setSavingAvatar(false);
    }
  };

  // Handle file selection for photo upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP.');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La photo ne doit pas dépasser 5MB.');
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload photo
  const handleUploadPhoto = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner une photo');
      return;
    }
    
    try {
      setUploadingPhoto(true);
      const result = await api.uploadProfilePhoto(currentUser.id, selectedFile);
      
      // Update profile data - backend now returns data URL for MongoDB storage
      const photoDataUrl = result.photo_url;
      setProfileData(prev => ({ 
        ...prev, 
        photo_url: photoDataUrl,
        photo_data: photoDataUrl.startsWith('data:') ? photoDataUrl : null,
        has_photo: true
      }));
      
      // Update current user in context
      setCurrentUser(prev => ({
        ...prev,
        photo_url: photoDataUrl,
        photo_data: photoDataUrl.startsWith('data:') ? photoDataUrl : null,
        has_photo: true
      }));
      
      // Clear selection
      setSelectedFile(null);
      setPhotoPreview(null);
      setShowAvatarDialog(false);
      
      toast.success('Photo de profil mise à jour avec succès');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Delete photo
  const handleDeletePhoto = async () => {
    if (!profileData.photo_url && !profileData.photo_data && !profileData.has_photo) return;
    
    try {
      setDeletingPhoto(true);
      await api.deleteProfilePhoto(currentUser.id);
      
      // Update profile data
      setProfileData(prev => ({ ...prev, photo_url: null, photo_data: null, has_photo: false }));
      
      // Update current user in context
      setCurrentUser(prev => ({
        ...prev,
        photo_url: null,
        photo_data: null,
        has_photo: false
      }));
      
      // Close the dialog after successful deletion
      setShowAvatarDialog(false);
      
      toast.success('Photo de profil supprimée');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression de la photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  // Cancel photo selection
  const handleCancelPhotoSelection = () => {
    setSelectedFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ===================== 2FA FUNCTIONS =====================
  
  const handleStart2FASetup = async () => {
    if (!twoFAPassword) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }
    
    try {
      setSetting2FA(true);
      const result = await api.setup2FA(twoFAPassword);
      setTwoFASetupData(result);
      setTwoFAStep('setup');
      toast.success('Configuration 2FA initiée');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la configuration 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleConfirm2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    
    try {
      setSetting2FA(true);
      await api.confirm2FASetup(twoFACode);
      setTwoFAEnabled(true);
      setShow2FADialog(false);
      reset2FADialog();
      toast.success('Authentification à deux facteurs activée !');
    } catch (error) {
      toast.error(error.message || 'Code invalide');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFAPassword) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }
    
    try {
      setDisabling2FA(true);
      await api.disable2FA(twoFAPassword);
      setTwoFAEnabled(false);
      setShow2FADialog(false);
      reset2FADialog();
      toast.success('Authentification à deux facteurs désactivée');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la désactivation');
    } finally {
      setDisabling2FA(false);
    }
  };

  const reset2FADialog = () => {
    setTwoFAPassword('');
    setTwoFACode('');
    setTwoFASetupData(null);
    setTwoFAStep('password');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  // Helper function to render avatar
  const renderAvatar = (avatarId, size = 'large', initials = '', photoUrl = null) => {
    const sizeClasses = {
      small: 'w-10 h-10 text-lg',
      medium: 'w-12 h-12 text-xl',
      large: 'w-20 h-20 text-3xl'
    };
    
    // If user has photo_data (base64), use it directly
    if (profileData.photo_data && profileData.photo_data.startsWith('data:')) {
      return (
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-200`}>
          <img 
            src={profileData.photo_data} 
            alt="Photo de profil" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="w-full h-full bg-blue-500 flex items-center justify-center text-white font-bold">${initials || currentUser?.prenom?.charAt(0) || 'U'}</div>`;
            }}
          />
        </div>
      );
    }
    
    // Fallback: If user has a photo URL (file-based), display it
    if (photoUrl || profileData.photo_url) {
      const url = photoUrl || api.getProfilePhotoUrl(currentUser?.id);
      return (
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-200`}>
          <img 
            src={url} 
            alt="Photo de profil" 
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="w-full h-full bg-blue-500 flex items-center justify-center text-white font-bold">${initials || currentUser?.prenom?.charAt(0) || 'U'}</div>`;
            }}
          />
        </div>
      );
    }
    
    const avatar = PREDEFINED_AVATARS.find(a => a.id === avatarId);
    
    if (avatar) {
      if (avatar.initials) {
        return (
          <div className={`${sizeClasses[size]} ${avatar.color} rounded-full flex items-center justify-center text-white font-bold`}>
            {initials || currentUser?.prenom?.charAt(0) || 'U'}
          </div>
        );
      } else if (avatar.icon) {
        return (
          <div className={`${sizeClasses[size]} bg-gray-100 rounded-full flex items-center justify-center`}>
            <span className={size === 'large' ? 'text-4xl' : size === 'medium' ? 'text-2xl' : 'text-xl'}>{avatar.icon}</span>
          </div>
        );
      }
    }
    
    // Default avatar
    return (
      <div className={`${sizeClasses[size]} bg-blue-500 rounded-full flex items-center justify-center text-white font-bold`}>
        {initials || currentUser?.prenom?.charAt(0) || 'U'}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
        <p className="text-gray-500">Gérez vos informations personnelles et votre mot de passe</p>
      </div>

      {/* User Summary Card with Avatar */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            {/* Avatar avec bouton de modification */}
            <div className="relative group">
              {renderAvatar(profileData.avatar, 'large')}
              <button
                onClick={() => setShowAvatarDialog(true)}
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="change-avatar-btn"
              >
                <Camera className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{currentUser?.prenom} {currentUser?.nom}</h2>
              <p className="text-gray-500">{currentUser?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={getRoleBadgeColor(currentUser?.role)}>
                  <Shield className="h-3 w-3 mr-1" />
                  {currentUser?.role}
                </Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={() => setShowAvatarDialog(true)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Changer l'avatar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Avatar Selection Dialog */}
      <Dialog open={showAvatarDialog} onOpenChange={(open) => {
        setShowAvatarDialog(open);
        if (!open) {
          handleCancelPhotoSelection();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Choisir votre avatar
            </DialogTitle>
            <DialogDescription>
              Uploadez votre photo ou sélectionnez un avatar prédéfini
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={avatarTab} onValueChange={setAvatarTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="photo" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Ma Photo
              </TabsTrigger>
              <TabsTrigger value="predefined" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Avatars
              </TabsTrigger>
            </TabsList>
            
            {/* Photo Upload Tab */}
            <TabsContent value="photo" className="py-4">
              <div className="space-y-4">
                {/* Current/Preview Photo */}
                <div className="flex justify-center">
                  {photoPreview ? (
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-500">
                        <img 
                          src={photoPreview} 
                          alt="Aperçu" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={handleCancelPhotoSelection}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (profileData.photo_url || profileData.photo_data || profileData.has_photo) ? (
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-300">
                        <img 
                          src={profileData.photo_data && profileData.photo_data.startsWith('data:') 
                            ? profileData.photo_data 
                            : api.getProfilePhotoUrl(currentUser?.id)} 
                          alt="Photo actuelle" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Hide image on error and show fallback
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-500">
                        Photo actuelle
                      </Badge>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400">
                      <Camera className="h-8 w-8 mb-2" />
                      <span className="text-xs">Pas de photo</span>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="space-y-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="photo-file-input"
                  />
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="select-photo-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {photoPreview ? 'Changer la photo' : 'Sélectionner une photo'}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Formats acceptés: JPG, PNG, GIF, WEBP • Taille max: 5MB
                  </p>
                  
                  {(profileData.photo_url || profileData.photo_data || profileData.has_photo) && !photoPreview && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="w-full"
                      onClick={handleDeletePhoto}
                      disabled={deletingPhoto}
                      data-testid="delete-photo-btn"
                    >
                      {deletingPhoto ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Suppression...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer ma photo
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {/* Upload Button */}
                {photoPreview && (
                  <Button 
                    className="w-full"
                    onClick={handleUploadPhoto}
                    disabled={uploadingPhoto}
                    data-testid="upload-photo-btn"
                  >
                    {uploadingPhoto ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Enregistrer cette photo
                      </>
                    )}
                  </Button>
                )}
              </div>
            </TabsContent>
            
            {/* Predefined Avatars Tab */}
            <TabsContent value="predefined" className="py-4">
              {/* Preview */}
              <div className="flex justify-center mb-6">
                {renderAvatar(selectedAvatar, 'large', '', null)}
              </div>
              
              {/* Avatars avec couleurs */}
              <div className="mb-4">
                <Label className="text-sm text-gray-500 mb-2 block">Couleurs avec initiales</Label>
                <div className="grid grid-cols-8 gap-2">
                  {PREDEFINED_AVATARS.filter(a => a.initials).map(avatar => (
                    <button
                      key={avatar.id}
                      onClick={() => setSelectedAvatar(avatar.id)}
                      className={`relative w-10 h-10 ${avatar.color} rounded-full flex items-center justify-center text-white font-bold text-sm transition-transform hover:scale-110 ${
                        selectedAvatar === avatar.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                      }`}
                      data-testid={`avatar-${avatar.id}`}
                    >
                      {currentUser?.prenom?.charAt(0) || 'U'}
                      {selectedAvatar === avatar.id && (
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-0.5">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Avatars métiers */}
              <div>
                <Label className="text-sm text-gray-500 mb-2 block">Avatars métiers</Label>
                <div className="grid grid-cols-8 gap-2">
                  {PREDEFINED_AVATARS.filter(a => a.icon).map(avatar => (
                    <button
                      key={avatar.id}
                      onClick={() => setSelectedAvatar(avatar.id)}
                      title={avatar.label}
                      className={`relative w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl transition-transform hover:scale-110 hover:bg-gray-200 ${
                        selectedAvatar === avatar.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                      }`}
                      data-testid={`avatar-${avatar.id}`}
                    >
                      {avatar.icon}
                      {selectedAvatar === avatar.id && (
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-0.5">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Note about photo */}
              {profileData.photo_url && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Vous avez une photo de profil. Sélectionner un avatar prédéfini ne supprimera pas votre photo, mais l'avatar sera affiché à la place.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
          
          {avatarTab === 'predefined' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setSelectedAvatar(profileData.avatar);
                setShowAvatarDialog(false);
              }}>
                Annuler
              </Button>
              <Button onClick={handleSaveAvatar} disabled={savingAvatar}>
                {savingAvatar ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informations personnelles
            </CardTitle>
            <CardDescription>
              Modifiez vos informations de profil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={profileData.prenom}
                    onChange={(e) => handleProfileChange('prenom', e.target.value)}
                    placeholder="Votre prénom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={profileData.nom}
                    onChange={(e) => handleProfileChange('nom', e.target.value)}
                    placeholder="Votre nom"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">
                  <Mail className="h-4 w-4 inline mr-2" />
                  Adresse email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  placeholder="votre.email@exemple.fr"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telephone">
                  <Phone className="h-4 w-4 inline mr-2" />
                  Téléphone
                </Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={profileData.telephone}
                  onChange={(e) => handleProfileChange('telephone', e.target.value)}
                  placeholder="06 12 34 56 78"
                />
              </div>
              
              <Separator />
              
              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer les modifications
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Changer le mot de passe
            </CardTitle>
            <CardDescription>
              Mettez à jour votre mot de passe de connexion
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Mot de passe actuel *</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Minimum 6 caractères</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordData.newPassword && passwordData.confirmPassword && (
                  <div className="flex items-center gap-2 mt-1">
                    {passwordData.newPassword === passwordData.confirmPassword ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-green-600">Les mots de passe correspondent</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <span className="text-xs text-red-600">Les mots de passe ne correspondent pas</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <Separator />
              
              <Button 
                type="submit" 
                variant="outline" 
                className="w-full" 
                disabled={changingPassword}
              >
                {changingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Modification en cours...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Changer le mot de passe
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* 2FA Security Section */}
      {twoFAAvailable && (
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              Authentification à deux facteurs (2FA)
            </CardTitle>
            <CardDescription>
              Ajoutez une couche de sécurité supplémentaire à votre compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${twoFAEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {twoFAEnabled ? (
                    <ShieldCheck className="h-6 w-6 text-green-600" />
                  ) : (
                    <ShieldOff className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium">
                    {twoFAEnabled ? '2FA Activée' : '2FA Non activée'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {twoFAEnabled 
                      ? 'Votre compte est protégé par une vérification en deux étapes'
                      : 'Activez la 2FA pour sécuriser votre compte'
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant={twoFAEnabled ? "destructive" : "default"}
                onClick={() => setShow2FADialog(true)}
                data-testid="toggle-2fa-btn"
              >
                {twoFAEnabled ? (
                  <>
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Désactiver
                  </>
                ) : (
                  <>
                    <Smartphone className="h-4 w-4 mr-2" />
                    Activer la 2FA
                  </>
                )}
              </Button>
            </div>
            
            {twoFAEnabled && (
              <Alert className="mt-4 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Lors de votre prochaine connexion, vous devrez entrer un code généré par votre application d'authentification.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* 2FA Setup/Disable Dialog */}
      <Dialog open={show2FADialog} onOpenChange={(open) => {
        setShow2FADialog(open);
        if (!open) reset2FADialog();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {twoFAEnabled ? (
                <>
                  <ShieldOff className="h-5 w-5 text-red-600" />
                  Désactiver la 2FA
                </>
              ) : (
                <>
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  Activer la 2FA
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {twoFAEnabled 
                ? 'Entrez votre mot de passe pour désactiver l\'authentification à deux facteurs'
                : 'Configurez une application d\'authentification pour sécuriser votre compte'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Disable 2FA - Simple password form */}
          {twoFAEnabled ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Mot de passe actuel</Label>
                <Input
                  type="password"
                  value={twoFAPassword}
                  onChange={(e) => setTwoFAPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  data-testid="2fa-disable-password"
                />
              </div>
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  La désactivation de la 2FA rendra votre compte moins sécurisé.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            // Enable 2FA - Multi-step process
            <div className="py-4">
              {twoFAStep === 'password' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mot de passe actuel</Label>
                    <Input
                      type="password"
                      value={twoFAPassword}
                      onChange={(e) => setTwoFAPassword(e.target.value)}
                      placeholder="Confirmez votre identité"
                      data-testid="2fa-setup-password"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Pour activer la 2FA, vous aurez besoin d'une application comme Google Authenticator, Authy ou Microsoft Authenticator.
                  </p>
                </div>
              )}
              
              {twoFAStep === 'setup' && twoFASetupData && (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    {twoFASetupData.qr_code && (
                      <img 
                        src={twoFASetupData.qr_code} 
                        alt="QR Code 2FA" 
                        className="w-48 h-48 border rounded-lg"
                      />
                    )}
                  </div>
                  
                  <p className="text-sm text-center text-gray-600">
                    Scannez ce QR code avec votre application d'authentification
                  </p>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-500">Ou entrez cette clé manuellement :</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-100 rounded text-xs font-mono break-all">
                        {twoFASetupData.secret}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(twoFASetupData.secret)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {twoFASetupData.backup_codes && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <p className="font-medium mb-2">Codes de secours :</p>
                        <div className="grid grid-cols-2 gap-1 text-xs font-mono">
                          {twoFASetupData.backup_codes.map((code, i) => (
                            <span key={i} className="bg-gray-100 p-1 rounded">{code}</span>
                          ))}
                        </div>
                        <p className="mt-2 text-xs">Conservez ces codes en lieu sûr. Ils permettent de récupérer l'accès si vous perdez votre téléphone.</p>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-2 pt-4">
                    <Label>Code de vérification</Label>
                    <Input
                      type="text"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Entrez le code à 6 chiffres"
                      maxLength={6}
                      className="text-center text-2xl tracking-widest"
                      data-testid="2fa-verify-code"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShow2FADialog(false);
              reset2FADialog();
            }}>
              Annuler
            </Button>
            
            {twoFAEnabled ? (
              <Button 
                variant="destructive"
                onClick={handleDisable2FA}
                disabled={disabling2FA || !twoFAPassword}
              >
                {disabling2FA ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Désactivation...
                  </>
                ) : (
                  'Désactiver la 2FA'
                )}
              </Button>
            ) : twoFAStep === 'password' ? (
              <Button 
                onClick={handleStart2FASetup}
                disabled={setting2FA || !twoFAPassword}
              >
                {setting2FA ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Configuration...
                  </>
                ) : (
                  'Continuer'
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleConfirm2FA}
                disabled={setting2FA || twoFACode.length !== 6}
              >
                {setting2FA ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Activer la 2FA'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Information (Read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Informations du compte
          </CardTitle>
          <CardDescription>
            Ces informations sont gérées par l'administrateur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Rôle</p>
              <p className="font-medium">{currentUser?.role}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Secteur</p>
              <p className="font-medium">{currentUser?.secteur || '-'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Statut du compte</p>
              <Badge className={currentUser?.actif !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {currentUser?.actif !== false ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
          </div>
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Pour modifier votre rôle ou votre secteur, veuillez contacter votre administrateur.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* RGPD Section */}
      <RGPDSection currentUser={currentUser} />
    </div>
  );
}

// ===================== RGPD SECTION COMPONENT =====================

function RGPDSection({ currentUser }) {
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteForm, setDeleteForm] = useState({
    confirmation: '',
    password: '',
    reason: ''
  });
  const [deleting, setDeleting] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [settings, setSettings] = useState({
    dpo_email: 'dpo@jeldwen.com'
  });

  useEffect(() => {
    loadDeletionRequests();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getPublicSettings();
      if (data?.dpo_email) {
        setSettings(prev => ({ ...prev, dpo_email: data.dpo_email }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadDeletionRequests = async () => {
    try {
      const response = await api.getDeletionRequestStatus();
      setDeletionRequests(response.requests || []);
    } catch (error) {
      console.error('Error loading deletion requests:', error);
    }
  };

  const handleExportData = async () => {
    try {
      setExporting(true);
      const blob = await api.exportPersonalData();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mes_donnees_rgpd_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Export de vos données téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export des données');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteRequest = async (e) => {
    e.preventDefault();
    
    if (deleteForm.confirmation !== 'SUPPRIMER MES DONNEES') {
      toast.error('Veuillez saisir exactement "SUPPRIMER MES DONNEES" pour confirmer');
      return;
    }
    
    try {
      setDeleting(true);
      await api.requestDataDeletion(
        deleteForm.confirmation,
        deleteForm.password,
        deleteForm.reason
      );
      toast.success('Votre demande de suppression a été enregistrée');
      setShowDeleteDialog(false);
      setDeleteForm({ confirmation: '', password: '', reason: '' });
      loadDeletionRequests();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la demande de suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <Shield className="h-5 w-5" />
          Vos droits RGPD
        </CardTitle>
        <CardDescription>
          Conformément au Règlement Général sur la Protection des Données, vous disposez de droits sur vos données personnelles.
          <Link to="/politique-confidentialite" className="text-blue-600 hover:underline ml-1">
            Voir notre politique de confidentialité
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Export Data */}
        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Download className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Droit d'accès et portabilité</span>
              <Badge variant="outline" className="text-xs">Articles 15 & 20</Badge>
            </div>
            <p className="text-sm text-gray-600">
              Téléchargez une copie de toutes vos données personnelles au format JSON.
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleExportData}
            disabled={exporting}
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Export...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Exporter mes données
              </>
            )}
          </Button>
        </div>

        {/* Rectification Info */}
        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-green-600" />
              <span className="font-medium">Droit de rectification</span>
              <Badge variant="outline" className="text-xs">Article 16</Badge>
            </div>
            <p className="text-sm text-gray-600">
              Vous pouvez modifier vos informations personnelles directement depuis cette page. 
              Pour les données gérées par l'administrateur, contactez le service RH.
            </p>
          </div>
        </div>

        {/* Delete Data */}
        <div className="flex items-start justify-between p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="h-4 w-4 text-red-600" />
              <span className="font-medium text-red-700">Droit à l'effacement</span>
              <Badge variant="outline" className="text-xs border-red-300 text-red-700">Article 17</Badge>
            </div>
            <p className="text-sm text-gray-600">
              Demandez la suppression de vos données personnelles. 
              <span className="text-amber-600 font-medium"> Note: certaines données peuvent être conservées pour obligations légales.</span>
            </p>
          </div>
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Demander la suppression
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Demande de suppression de données
                </DialogTitle>
                <DialogDescription>
                  Cette action est irréversible. Certaines données peuvent être conservées pour respecter nos obligations légales (Code du travail, conservation des documents de formation).
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleDeleteRequest} className="space-y-4">
                <div className="space-y-2">
                  <Label>Raison de la demande (optionnel)</Label>
                  <Textarea
                    value={deleteForm.reason}
                    onChange={(e) => setDeleteForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Pourquoi souhaitez-vous supprimer vos données ?"
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Confirmation *</Label>
                  <Input
                    value={deleteForm.confirmation}
                    onChange={(e) => setDeleteForm(prev => ({ ...prev, confirmation: e.target.value }))}
                    placeholder="Tapez: SUPPRIMER MES DONNEES"
                  />
                  <p className="text-xs text-gray-500">
                    Saisissez exactement <span className="font-mono bg-gray-100 px-1">SUPPRIMER MES DONNEES</span> pour confirmer
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Mot de passe actuel *</Label>
                  <Input
                    type="password"
                    value={deleteForm.password}
                    onChange={(e) => setDeleteForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Votre mot de passe pour confirmer"
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDeleteDialog(false)}>
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    variant="destructive"
                    disabled={deleting || deleteForm.confirmation !== 'SUPPRIMER MES DONNEES' || !deleteForm.password}
                  >
                    {deleting ? 'Envoi...' : 'Confirmer la suppression'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Deletion requests history */}
        {deletionRequests.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Historique des demandes de suppression</h4>
            <div className="space-y-2">
              {deletionRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm">
                  <div>
                    <span className="text-gray-600">
                      {new Date(request.date_demande).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <Badge className={
                    request.status === 'completed' ? 'bg-green-100 text-green-800' :
                    request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    request.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                    'bg-amber-100 text-amber-800'
                  }>
                    {request.status === 'pending' ? 'En attente' :
                     request.status === 'approved' ? 'Approuvée' :
                     request.status === 'rejected' ? 'Refusée' :
                     request.status === 'completed' ? 'Terminée' : request.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact DPO */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-900">Contact DPO</span>
          </div>
          <p className="text-sm text-blue-800">
            Pour toute question relative à vos données personnelles ou pour exercer vos autres droits (limitation, opposition), 
            contactez notre Délégué à la Protection des Données : <a href={`mailto:${settings.dpo_email}`} className="underline font-medium">{settings.dpo_email}</a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
